/**
 * EarningsRecords — detailed earnings breakdown with inline fee visualization.
 *
 * Instead of expandable table rows, each period is rendered as a card
 * with an inline horizontal progress bar showing fee% vs partner%.
 * Desktop uses a clean table with mini progress bars inline.
 *
 * Features:
 * - Group-by toggle (week/month/vehicle)
 * - Date range picker (shared with Earnings analytics)
 * - Card layout (mobile) with progress bars
 * - Table layout (desktop) with inline fee bars
 * - Pagination (15 per page)
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, formatCurrency, CHART_COLORS,
} from '@/lib/format';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { startOfMonth, subMonths, endOfMonth, addDays, format } from 'date-fns';
import { captureError } from '@/lib/logger';
import SectionDrawer from '@/components/navigation/SectionDrawer';
import { useNavigate } from 'react-router-dom';
import { ListPagination } from '@/components/ListPagination';
import { Search } from 'lucide-react';

const PAGE_SIZE = 15;

/** Convert ISO week key like '2026-15' to its Monday Date */
function weekKeyToMonday(groupKey: string): Date {
    const [yearStr, weekStr] = groupKey.split('-');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStr, 10);
    // ISO 8601: Week 1 contains Jan 4. Monday of week 1:
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7; // Mon=1..Sun=7
    const mondayW1 = addDays(jan4, 1 - dayOfWeek);
    return addDays(mondayW1, (week - 1) * 7);
}

/** Convert ISO week key like '2026-15' to 'Apr 7 – Apr 13, 2026' */
function weekRangeLabel(groupKey: string): string {
    const monday = weekKeyToMonday(groupKey);
    const sunday = addDays(monday, 6);
    const sameYear = monday.getFullYear() === sunday.getFullYear();
    if (sameYear) {
        return `${format(monday, 'MMM d')} – ${format(sunday, 'MMM d, yyyy')}`;
    }
    return `${format(monday, 'MMM d, yyyy')} – ${format(sunday, 'MMM d, yyyy')}`;
}

interface EarningsRow {
    group_key: string;
    group_label: string;
    vehicle_count: number;
    gross_repayments: number;
    management_fee_amount: number;
    partner_portion: number;
    maintenance_cost: number;
    net_earnings: number;
    repayment_count: number;
}

/**
 * FeeBar — inline horizontal stacked bar showing partner% vs fee%
 */
function FeeBar({ gross, fee: _fee, partnerPortion }: { gross: number; fee: number; partnerPortion: number }) {
    if (gross <= 0) return <div className="h-2 rounded-full bg-muted w-full" />;
    const partnerPct = (partnerPortion / gross) * 100;
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
                <div
                    className="h-full rounded-l-full transition-all duration-300"
                    style={{ width: `${partnerPct}%`, backgroundColor: CHART_COLORS.slate900 }}
                />
                <div
                    className="h-full rounded-r-full transition-all duration-300"
                    style={{ width: `${100 - partnerPct}%`, backgroundColor: CHART_COLORS.slate300 }}
                />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right shrink-0">
                {partnerPct.toFixed(2)}%
            </span>
        </div>
    );
}

interface EarningsTotals {
    gross: number;
    fees: number;
    earnings: number;
    avgRate: number;
    count: number;
    weekCount: number;
}

export default function EarningsRecords() {
    const { partnerId, signOut } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth');
    };

    const handleNavChange = (section: string) => {
        navigate(`/${section}`);
    };

    // Fetch earnings data with server-side pagination + search
    const { data: earningsResponse, isLoading } = useQuery({
        queryKey: ['partner-earnings-records', partnerId, fromInstant, toInstant, currentPage, debouncedSearch],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'week',
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                p_search: debouncedSearch || null,
            });
            if (error) { captureError(error); throw error; }
            const rows = data as (EarningsRow & { total_groups: number })[];
            return {
                rows,
                totalItems: rows.length > 0 ? rows[0].total_groups : 0,
            };
        },
        enabled: !!partnerId,
    });

    // Aggregate totals for stat cards (unpaginated)
    const { data: totals, isLoading: totalsLoading } = useQuery({
        queryKey: ['partner-earnings-totals', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'week',
            });
            if (error) { captureError(error); throw error; }
            const rows = data as EarningsRow[];
            if (!rows || rows.length === 0) {
                return { gross: 0, fees: 0, earnings: 0, avgRate: 0, count: 0, weekCount: 0 } as EarningsTotals;
            }
            const gross = rows.reduce((s, r) => s + r.gross_repayments, 0);
            const fees = rows.reduce((s, r) => s + r.management_fee_amount, 0);
            const earnings = rows.reduce((s, r) => s + r.partner_portion, 0);
            const count = rows.reduce((s, r) => s + r.repayment_count, 0);
            return {
                gross,
                fees,
                earnings,
                avgRate: gross > 0 ? (fees / gross * 100) : 0,
                count,
                weekCount: rows.length,
            } as EarningsTotals;
        },
        enabled: !!partnerId,
    });

    // Avg weekly earnings from payout summary (excludes partial first-week grace periods)
    const { data: payoutSummary } = useQuery({
        queryKey: ['partner-payout-summary-avg', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_payout_summary', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) { captureError(error); throw error; }
            return (data as { avg_weekly_partner_portion: number }[])?.[0] ?? null;
        },
        enabled: !!partnerId,
    });

    // Reset page on filter changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, monthRange.from, monthRange.to]);

    const paginatedData = earningsResponse?.rows ?? [];
    const totalItems = earningsResponse?.totalItems ?? 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);



    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer
                    activeSection="finance/earnings"
                    onSectionChange={handleNavChange}
                    onSignOut={handleSignOut}
                />
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight mb-2">Earnings</h1>
                    <p className="text-muted-foreground">Detailed breakdown with fee visualization per period.</p>
                </div>
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Stat Cards */}
            {totalsLoading ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
            ) : (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    <StatCard title="Gross Repayments" value={<CurrencyDisplay amount={totals?.gross ?? 0} />} description={`Total collected from ${totals?.count ?? 0} driver repayments`} />
                    <StatCard title="Management Fees" value={<CurrencyDisplay amount={totals?.fees ?? 0} />} description={`Primelane's fee at an avg rate of ${(totals?.avgRate ?? 0).toFixed(2)}%`} />
                    <StatCard title="Your Earnings" value={<CurrencyDisplay amount={totals?.earnings ?? 0} />} description="Your share after management fees are deducted" />
                    <StatCard title="Retention Rate" value={`${((totals?.gross ?? 0) > 0 ? ((totals!.earnings / totals!.gross) * 100) : 0).toFixed(2)}%`} description="Percentage of gross repayments you keep" />
                    <StatCard title="Avg Weekly Earnings" value={<CurrencyDisplay amount={payoutSummary?.avg_weekly_partner_portion ?? 0} />} description="Your average payout per week across all vehicles" />
                </div>
            )}

            <Card className="border-0">
                <CardContent className="space-y-4">
                    {/* Search + Pagination */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by week..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        {totalPages > 1 && (
                            <ListPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>

                    {/* Content */}
                    {isLoading ? (
                        <div className="space-y-3">
                            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
                        </div>
                    ) : paginatedData.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground">
                            No earnings data for the selected period.
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="overflow-x-auto hidden lg:block">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[220px]">Week</TableHead>
                                                <TableHead className="text-right">Vehicles</TableHead>

                                                <TableHead className="text-right">Gross</TableHead>
                                                <TableHead className="w-[200px]">Split</TableHead>
                                                <TableHead className="text-right">Fee</TableHead>
                                                <TableHead className="text-right">Your Earnings</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {paginatedData.map((row) => {
                                                const feeRate = row.gross_repayments > 0
                                                    ? (row.management_fee_amount / row.gross_repayments * 100)
                                                    : 0;

                                                return (
                                                    <TableRow
                                                        key={row.group_key}
                                                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                                                        onClick={() => navigate(`/finance/earnings/week?week=${format(weekKeyToMonday(row.group_key), 'yyyy-MM-dd')}`)}
                                                    >
                                                        <TableCell className="whitespace-nowrap">
                                                            <div className="font-medium">{weekRangeLabel(row.group_key)}</div>
                                                            <div className="text-xs text-muted-foreground">Week {row.group_key.split('-')[1]}</div>
                                                        </TableCell>
                                                        <TableCell className="text-right tabular-nums">{row.vehicle_count}</TableCell>

                                                        <TableCell className="text-right tabular-nums">{formatCurrency(row.gross_repayments)}</TableCell>
                                                        <TableCell>
                                                            <FeeBar
                                                                gross={row.gross_repayments}
                                                                fee={row.management_fee_amount}
                                                                partnerPortion={row.partner_portion}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="text-sm tabular-nums text-muted-foreground">
                                                                {formatCurrency(row.management_fee_amount)}
                                                                <span className="ml-1 text-xs">({feeRate.toFixed(2)}%)</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right font-medium tabular-nums">
                                                            {formatCurrency(row.partner_portion)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                            </div>

                            {/* Mobile Card Layout */}
                            <div className="lg:hidden space-y-3">
                                {paginatedData.map((row) => {
                                    const feeRate = row.gross_repayments > 0
                                        ? (row.management_fee_amount / row.gross_repayments * 100)
                                        : 0;

                                    return (
                                        <Card
                                            key={row.group_key}
                                            className="border border-border/40 cursor-pointer hover:bg-muted/30 transition-colors"
                                            onClick={() => navigate(`/finance/earnings/week?week=${format(weekKeyToMonday(row.group_key), 'yyyy-MM-dd')}`)}
                                        >
                                            <CardContent className="p-4 space-y-3">
                                                {/* Period header */}
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium">{weekRangeLabel(row.group_key)}</div>
                                                        <div className="text-xs text-muted-foreground">Week {row.group_key.split('-')[1]}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {row.vehicle_count} vehicle{row.vehicle_count !== 1 ? 's' : ''} · {row.repayment_count} repayments
                                                    </div>
                                                </div>

                                                {/* Fee split bar */}
                                                <FeeBar
                                                    gross={row.gross_repayments}
                                                    fee={row.management_fee_amount}
                                                    partnerPortion={row.partner_portion}
                                                />

                                                {/* Values row */}
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">Gross</div>
                                                        <div className="text-sm font-medium tabular-nums">{formatCurrency(row.gross_repayments)}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-muted-foreground">Fee ({feeRate.toFixed(2)}%)</div>
                                                        <div className="text-sm text-muted-foreground tabular-nums">{formatCurrency(row.management_fee_amount)}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-xs text-muted-foreground">Yours</div>
                                                        <div className="text-sm font-semibold tabular-nums">{formatCurrency(row.partner_portion)}</div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
